import { isEqual, isEmpty, flattenDeep } from 'lodash'
import { TimeoutUtil } from 'src/utils/helper/timer'
import { mapActions } from 'vuex'
import Misc from 'src/utils/helper/misc'
import { errors as submissionErrors } from 'src/pages/submissions/script/common.data'
import { Toaster } from 'src/utils/helper/toaster'

import { adobeCjaEventEmitter } from '../../../../../../../modules/adobe-cja/utils'
import { CJA_EVENTS, LINK_TYPES } from '../../../../../../../modules/adobe-cja/constants'
import designOrder, { importOrder } from '../sortData/data'

export default {
  ...mapActions('binaries', ['getAll']),
  mouseleave(e) {
    if (e.target.scrollLeft !== 0) {
      e.target.scrollLeft = 0
    }
  },
  /**
   * Check if the input text is in all caps
   * @param {string} text - The text to check
   * @returns {boolean} - True if text is all caps, false otherwise
   */
  isAllCaps(text) {
    if (!text || typeof text !== 'string') return false
    // Check if text has letters and all letters are uppercase
    const hasLetters = /[a-zA-Z]/.test(text)
    const allUppercase = text === text.toUpperCase()
    return hasLetters && allUppercase
  },
  /**
   * Convert text to title case
   * @param {string} text - The text to convert
   * @returns {string} - Text converted to title case
   */
  toTitleCase(text) {
    if (!text || typeof text !== 'string') return text
    return text.toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
  },
  bannedWordforFamilyName: false,
  bannedWordforFamilyNameLive: false,
  setFamily() {
    return new Promise(resolve => {
      this.family.name = this.submissionFamilyName
      this.family.uniqueName = this.submissionFamilyURLName
      this.family.pimFamilyId = this.submissionPimFamilyId
      resolve()
    })
  },
  getStoreBinary(b) {
    return this.$store.getters['binaries/list'].filter(
      item => item.id === b.binaryId && typeof item.id === 'string' && item.fontInfoComplete === true
    )[0]
  },
  hasResults() {
    return this.ready.state === 'set' && this.results.items.length > 0
  },
  resultsFamilyInit(isEmitted = false) {
    this.setSubscriptions()
    this.setFamily()
      .then(() => {
        if (isEmitted) {
          return this.getResults()
        }
        return this.getLiveResults()
      })
      .then(() => {
        this.ready.state = 'set'
        this.toggleUniqFamilyError()
      })
      .then(() => {
        if (isEmitted) {
          this.$events.$emit('on_load_validate_sections', 'upload:results')
        }
      })
  },
  setFontFamilyURL(event) {
    const family = { ...this.family };
    family.uniqueName = Misc.spaceTodashString(event.target.value.replace(/[,\\]/g, ''))
    family.uniqueName = Misc.normalizeDiacriticsOnString(family.uniqueName)
    this.family = family
  },
  checkUniqueURL() {
    this.family.editing = false
    this.syncFamilyNameWithStore()
    this.emitResultsUpdate()
    this.updateSubmission().then(() => {
      this.toggleUniqFamilyError()
    })
  },
  toggleUniqFamilyError() {
    if (!this.isFamilyUnique && !this.getLiveParam(this.submission)) {
      this.stepsHasErrorsUrl = true
    } else {
      this.stepsHasErrorsUrl = false
    }
  },
  getLiveParam(data) {
    if (data.hasOwnProperty('isLive')) {
      return data.isLive
    }
    return false
  },
  getBinariesData(force = false) {
    return new Promise(resolve => {
      this.getAll({
        submissionId: this.submission.id,
        force
      })
        .then(() => {
          return this.prepareCompareBinaries()
        })
        .then(() => {
          return this.setResults()
        })
        .then(resolve)
    })
  },
  getResults(force = false) {
    return new Promise(resolve => {
      this.getBinariesData(force)
        .then(() => {
          return this.compareBinaries()
        })
        .then(different => {
          if (different) {
            this.updateSubmission().then(this.emitSkuUpdate)
          }
          resolve()
        })
        .then(() => {
          for (let i = 0; i < this.results.items.length; i++) {
            const resultList = { ...this.results }
            resultList.items[i].styleNameURL = Misc.dashedString(this.results.items[i].styleName)
            this.results = resultList

            // removing the binaries which are having avStatus as false
            if (this.results.items[i].avStatus === 'false') {
              this.removeResult(i)
            }
          }
        })
    })
  },
  getLiveResults(force = false) {
    return new Promise(resolve => {
      this.getBinariesData(force)
        .then(() => {
          if (this.newBinaries.length > 0) {
            for (const val of this.newBinaries) {
              const index = Misc.findIndex(this.allBinaries, { id: val.id })
              this.$store.commit('binaries/clearNewBinaries', val.id)
              this.$store.commit('binaries/addNewBinaries', this.allBinaries[index])
            }
            this.$events.$emit('get_new_binaries')
          }
          return this.compareBinaries()
        })
        .then(() => {
          this.emitSkuUpdate()
          resolve()
        })
    })
  },
  setResults() {
    return new Promise(resolve => {
      if (this.$store.getters['binaries/list'].length === 0) {
        resolve()
      } else {
        this.addResults().then(() => {
          /**
           * If results set data.
           */
          if (this.results.items.length > 0) {
            this.setFamilyName()
            // Set initial style names using the formatted default style names
            this.updateStyleNamesAfterFamilyChange()
            // Emit update immediately after setting family name to trigger re-render
            this.emitResultsUpdate()
            this.awaitResultsComplete()
          }
          // Emit again for other components
          this.emitResultsUpdate()
          this.syncBinariesWithStore()
          /**
           * Done.
           */
          resolve()
        })
      }
    })
  },
  addResults() {
    return new Promise(resolve => {
      /**
       * Add results from $store binaries
       */
      const binaries = this.$store.getters['binaries/list']
      /**
       * Sort $store binaries by submission binary order.
       */
      const _idxOf = id => {
        return this.submissionBinaries
          .map(b => {
            return b.binaryId
          })
          .indexOf(id)
      }
      binaries.sort((a, b) => {
        return _idxOf(a.id) > _idxOf(b.id) ? 1 : -1
      })
      /**
       * Loop trough $store binaries and compare with submission and data results.
       */
      binaries.forEach((binary, idx) => {
        /**
         * Update data results if exists.
         */
        const found = this.results.items.some((r, index) => {
          if (r.id === binary.id) {
            binary.styleName = this.getStyleName(r.styleName, binary)
            if (this.results.items[index].hasOwnProperty('status')) {
              binary.status = this.results.items[index].status
              this.results.items[index] = {
                ...this.results.items[index],
                ...binary
              }
            }
            if (this.results.items[index].hasOwnProperty('oldBinary')) {
              binary.oldBinary = this.results.items[index].oldBinary
              this.results.items[index] = {
                ...this.results.items[index],
                ...binary
              }
            }
            if (this.results.items[index].hasOwnProperty('uniqueName')) {
              binary.uniqueName = this.results.items[index].uniqueName
              this.results.items[index] = {
                ...this.results.items[index],
                ...binary
              }
            } else {
              this.results.items[index] = binary
            }
            return true
          }
        })
        /**
         * If not push.
         */
        if (!found) {
          /**
           * Prefer styleName from submission data.
           */
          this.submissionBinaries.some(b => {
            if (b.binaryId === binary.id) {
              binary.styleName = this.getStyleName(b.styleName, binary)
              if (b.hasOwnProperty('uniqueName')) {
                binary.uniqueName = b.uniqueName
              }
              if (b.hasOwnProperty('status')) {
                binary.status = b.status
              }
              if (b.hasOwnProperty('oldBinary')) {
                binary.oldBinary = b.oldBinary
              }
              return true
            }
          })
          this.results.items.push(binary)
        }
      })
      /**
       * Sync with submission binaries
       */
      this.syncBinariesWithStore()
      /**
       * Done.
       */
      resolve()
    })
  },
  emitResultsUpdate() {
    /**
     * Maybe try to find a better way
     */
    this.$events.$emit('submission:results:update')
  },
  unsetCustomerMessage() {
    if (!this.isCustomerMsg(this.results)) {
      this.submissionCustomerMessage = ''
      this.$events.$emit('unset:customer:message')
    }
  },
  removeResult(key) {
    /**
     * Remove result from $store.
     * When done reset results.
     */

    const resultItem = { ...this.results.items[key] } // to get the id of the binary which will be deleted from newBinaries Store
    this.removeErrors(this.results.items, key)
    return new Promise(resolve => {
      this.$events.$emit('delete:single_package_sku', key)
      this.$store.commit('binaries/deleteNewBinaries', resultItem.id)
      this.$store
        .dispatch('binaries/deleteBinaries', {
          binaries: [this.results.items.splice(key, 1)[0]],
          id: this.submission.id
        })
        .then(() => {
          this.unsetCustomerMessage()
          this.syncSubmissionOnDeletion()
          this.handleCustomPackageValidation(true)
          resolve()
        })
    })
  },
  removeErrors(fonts, key) {
    this.removeFontId = fonts[key].id
    this.removePriceId = key
    this.removeErrorFromSubmissionErrors()
    this.$events.$emit('delete:price')
    if (isEmpty(this.results.items)) {
      submissionErrors.value = submissionErrors.value.filter(error => !Object.hasOwn(error, 'binary amount upload'))
    }
  },
  syncSubmissionOnDeletion(unsetSkuId = true) {
    this.syncBinariesWithStore()
    this.setFamilyName()
    this.syncFamilyNameWithStore()
    if (unsetSkuId) {
      this.setSkuId()
    }
    this.updateSubmission()
    this.emitResultsUpdate()
    this.emitSkuUpdate()
  },
  cancelLiveResults(deleteBinArray) {
    /**
     * Deletes the binaries which are added
     * When clicks cancel on live modal.
     */
    if (!deleteBinArray.includes('true')) {
      this.$store.commit('submissions/setLiveSpinner', true)
    }
    const delIds = []
    deleteBinArray.map(val => {
      const key = Misc.findIndex(this.results.items, { id: val })
      if (key !== -1) {
        this.$events.$emit('delete:single_package_sku', key)
        delIds.push(this.results.items.splice(key, 1)[0])
      }
    })
    this.$store
      .dispatch('binaries/deleteBinaries', {
        binaries: delIds,
        id: this.submission.id
      })
      .then(res => {
        this.syncSubmissionOnDeletion(false)
        this.$store.commit('binaries/emptyNewBinaries')
      })
  },
  removeLiveBinary(binaryArray) {
    this.$store.commit('submissions/setLiveSpinner', true)
    const load = []
    this.results.items = this.results.items.filter(item => item.avStatus != 'false')
    binaryArray.map(binaryObj => {
      if (binaryObj.oldMappedBId !== 'add') {
        const key = Misc.findIndex(this.results.items, {
          id: binaryObj.oldMappedBId
        })
        const oldUniqueName = String(this.results.items[key].uniqueName)
        load.push(this.results.items.splice(key, 1)[0])
        const oldKey = Misc.findIndex(this.results.items, {
          id: binaryObj.newBId
        })
        const resultItems = [...this.results.items]
        Misc.moveIndexinArray(resultItems, oldKey, key) // to maintain sort order
        resultItems[key].oldBinary = binaryObj.oldMappedBId
        resultItems[key].status = 'updated'
        resultItems[key].uniqueName = oldUniqueName
        this.results.items = resultItems
      } else {
        load.push('new')
        const key = Misc.findIndex(this.results.items, { id: binaryObj.newBId })
        const resultItems = [...this.results.items]
        resultItems[key].status = 'new'
        this.results.items = resultItems
      }
    })
    const ids = load.filter(v => v !== 'new')
    this.$store
      .dispatch('binaries/deleteBinaries', {
        binaries: ids,
        id: this.submission.id
      })
      .then(res => {
        this.syncSubmissionOnDeletion()
        this.$store.commit('binaries/emptyNewBinaries')
      })
  },
  emitSkuUpdate() {
    this.$events.$emit('update:external_sku')
  },
  updateSubmissionOnStyleNameBlur(newVal, element) {
    this.results.items.forEach(item => {
      submissionErrors.value = submissionErrors.value.filter(err => !err.hasOwnProperty(`style name ${item.id} upload`))
    })

    element.styleName = newVal.name

    this.emitResultsUpdate()

    if (isEmpty(this.styleNameErrors)) {
      this.updateSubmission('', '', this.bannedWordForStyle).then(() => {
        if (this.submission.isLive) {
          this.$events.$emit('check:style unique name', this.$store.getters['binaries/list'])
        }
      })
    }
  },
  awaitResultsComplete() {
    /**
     * If not all of the binaries have metaData or completed validation, fetch new data.
     * If no metaData, fetch faster.
     */
    if (this.submission.status == 'in_review') {
      return;
    }
    const hasAllMeta = this.$store.getters['binaries/hasAllMeta']
    const hasAllValidated = this.$store.getters['binaries/hasAllValidated']
    const hasAllSfdms = this.$store.getters['binaries/hasAllSfdms']
    const timeout = (hasAllMeta && hasAllSfdms) ? 30000 : 5000
    this.results.timer.clear()
    if ((!hasAllMeta || !hasAllValidated) && this.submission.status !== 'live') {
      this.results.timer.set(() => {
        this.getResults(true)
      }, timeout)
    }
    if (this.submission.status !== 'live' && hasAllMeta) {
      setTimeout(() => {
        this.sortFonts(true)
      }, 500)
    }
    if ((!hasAllMeta || !hasAllValidated) && this.submission.status === 'live') {
      this.results.timer.set(() => {
        this.getLiveResults(true)
      }, timeout)
    }
  },
  someResultComplete() {
    return this.$store.getters['binaries/hasSomeMeta']
  },
  isResultComplete(result) {
    return result.fontInfoComplete === true || result.hasCriticalErrors === true
  },
  hasResultError(result) {
    return result.validationResult?.errorLevel === 'ERROR'
  },
  hasResultWarning(result) {
    return result.validationResult?.errorLevel === 'WARN'
  },
  updateResultList() {
    this.fouDraggable.update(this.updateExSkuOnDrag)
  },
  updateExSkuOnDrag() {
    this.emitSkuUpdate()
    this.updateSubmission()
  },
  removeErrorFromSubmissionErrors() {
    if (!isEmpty(this.results.items)) {
      this.results.items.forEach(item => {
        submissionErrors.value = submissionErrors.value.filter(
          err => !Object.hasOwn(err, `style name ${item.id} upload`)
        )
        submissionErrors.value = submissionErrors.value.filter(
          err => !Object.hasOwn(err, `external sku ${item.id} upload`)
        )
      })
      submissionErrors.value = [...new Set(submissionErrors.value.map(itm => JSON.stringify(itm)))].map(i =>
        JSON.parse(i)
      )
    }
  },
  setFamilyName() {
    if (!this.family.editing) {
      if (this.results.items.length === 0) {
        this.family.name = null;
        this.submissionFamilyName = null;
      } else if (this.family.name === null || this.family.name === undefined) {
        let name = null
        this.results.items.some(item => {
          if (typeof item.familyName === 'string' && item.familyName !== '') {
            name = item.familyName
            return true
          }
        })
        this.family.name = name
      }
    }
  },
  familyNameFocus() {
    this.family.editing = true
    this.familyIntialValue = this.family.name

  },
  // Check if family name is in all caps and convert to title case
  convertFamilyNameToTitleCase() {
    if (this.family.name && this.isAllCaps(this.family.name)) {
      this.family.name = this.toTitleCase(this.family.name)
      
      // Show toast notification
      Toaster.notifyWarn(this.$t('pages.submissions.components.upload.results.family_name_all_caps_warning'))
    }
  },
  updateFamilyName() {
    if (!Object.hasOwn(this.errors, 'family name upload')) {
      submissionErrors.value = submissionErrors.value.filter(err => !err.hasOwnProperty('family name upload'))
    }

    if (isEmpty(this.errors) || this.familyIntialValue !== this.family.name) {
      if (
        (this.submission.status === 'live' && this.family.name !== this.familyIntialValue) ||
        this.submission.status !== 'live'
      ) {
        this.convertFamilyNameToTitleCase();
      }

      this.family.editing = false
      this.syncFamilyNameWithStore()
      // Update all style names based on the new family name
      this.updateStyleNamesAfterFamilyChange()
      this.emitResultsUpdate()
      this.updateSubmission('', '', (this.bannedWordforFamilyName || this.bannedWordforFamilyNameLive))
    }
  },
  /**
   * Update all style names when the family name changes
   * This ensures the UI reflects the new family name without requiring a refresh
   */
  updateStyleNamesAfterFamilyChange() {
    if (this.results?.items?.length > 0) {
      // Update each element's styleName property
      this.results.items.forEach(element => {
        // Recalculate the style name based on the new family name
        const newStyleName = this.getFormattedDefaultStyleName(element)
        // Update the element's styleName property directly
        element.styleName = newStyleName
      })

      // Sync the updated style names with the store
      this.syncBinariesWithStore()
    }
  },
  syncFamilyNameWithStore() {
    if (this.hasRole['foundrySupport']) {
      this.submissionFamily = {
        myfontsId: 0,
        name: this.family.name,
        uniqueName: Misc.normalizeDiacriticsOnString(Misc.strToLowerCase(this.family.uniqueName)),
        pimFamilyId: this.family.pimFamilyId
      };
      return;
    }

    if (this.family.name !== null) {
      const uniqueName = this.submission.isLive
        ? this.submissionFamilyURLName
        : Misc.normalizeDiacriticsOnString(Misc.dashedString(Misc.strToLowerCase(this.family.name)));

      this.submissionFamily = {
        myfontsId: 0,
        name: this.family.name,
        uniqueName: uniqueName,
        pimFamilyId: this.family.pimFamilyId
      };
    }
  },
  checkBinaryServices() {
    return new Promise(resolve => {
      this.binaryServices = ''
      this.results.items.forEach((binary, idx) => {
        const isValidBinary =
          binary.hasOwnProperty('validationResult') &&
          binary.validationResult.progress.hasOwnProperty('sectionsComplete') &&
          binary.validationResult.progress.sectionsComplete.includes('onMetadataComplete')
        if (!isValidBinary) {
          this.binaryServices = 'incomplete'
        }
      })
      resolve()
    })
  },
  showBinaryValidationProgress(binary) {
    const isValidBinary =
      binary.hasOwnProperty('validationResult') &&
      binary.validationResult.progress.hasOwnProperty('sectionsComplete') &&
      binary.validationResult.progress.sectionsComplete.includes('onMetadataComplete') &&
      binary.validationResult.progress.sectionsComplete.includes('onValidatorComplete') &&
      binary.validationResult.progress.sectionsComplete.includes('onSanitizerComplete')
    return !isValidBinary
  },
  syncBinariesWithStore() {
    this.submissionBinaries = this.results.items.map(r => {
      const obj = {
        binaryId: r.id,
        styleName: r.styleName
      }
      if (r.hasOwnProperty('status') && r.status === 'new') {
        obj.status = 'new'
      }
      if (r.hasOwnProperty('status') && r.status === 'updated') {
        obj.status = 'updated'
        obj.oldBinary = r.oldBinary
      }
      if (r.hasOwnProperty('uniqueName')) {
        obj.uniqueName = r.uniqueName
      }
      return obj
    })
    const _idxOf = this.getSubmissionBiranyIndex()
    this.$store.commit('binaries/syncOrder', _idxOf)
    this.checkBinaryServices()
  },
  getStyleName(name, binary) {
    return typeof name === 'string' ? name : binary.styleName || binary.fontInfo?.styleName || ''
  },
  getBinaryVersionText(binaryElement) {
    const rawVersionText = binaryElement?.fontInfo?.versionText
    if (rawVersionText == null) return null
    const trimmedVersion = String(rawVersionText).trim()
    return trimmedVersion === '' ? null : trimmedVersion
  },
  getBinaryVersionDisplay(binaryElement) {
    const versionText = this.getBinaryVersionText(binaryElement)
    if (!versionText) return null
    const maxDisplayLength = 7
    return versionText.length > maxDisplayLength
      ? `${versionText.slice(0, maxDisplayLength)}…`
      : versionText
  },
  getBinaryVersionTooltip(binaryElement) {
    const versionText = this.getBinaryVersionText(binaryElement)
    const maxDisplayLength = 7
    if (!versionText || versionText.length <= maxDisplayLength) return null
    return { content: versionText, placement: 'bottom' }
  },
  buildSubmission() {
    return new Promise(resolve => {
      /**handleErrorsForValidation
       * Set binaries
       */
      this.syncBinariesWithStore()
      /**
       * Set family
       */
      this.syncFamilyNameWithStore()
      /**
       * Check if assigned default layout font is still present
       * if not, set to first of collection
       */
      if (
        !this.submissionDefaultLayoutFont ||
        this.submissionBinaries.map(b => b.binaryId).indexOf(this.submissionDefaultLayoutFont.id) < 0
      ) {
        if (this.submissionBinaries.length > 0) {
          this.submissionDefaultLayoutFont = {
            id: this.submissionBinaries[0].binaryId,
            name: this.submissionBinaries[0].styleName
          }
        }
      }
      /**
       * Done.
       */
      resolve()
    })
  },
  sortFonts(autoSort = false) {
    if (!autoSort) {
      const slitPageName = window?.pageInfoGlobal?.pageInfo?.pageName?.split(':')
      let fontFormat = [...new Set(this.results.items.map(item => item.format))]
      fontFormat = fontFormat.join(':')
      const sortEventAdobePayload = {
        event: CJA_EVENTS.LINK_CLICK,
        font: [{
          fontFamily: this?.submission?.userMetaData?.family?.name || '',
          fontFoundry: this?.submission?.foundry?.name || '',
          fontFormat: fontFormat || '',
          isLive: this?.submission?.isLive || '',
          fontActionLocation: window?.pageInfoGlobal?.pageInfo?.pageName || '',
          submissionId: this?.submission?.id || '',
        }],
        links: {
          linkCategory: slitPageName ? slitPageName[slitPageName.length - 1] : '',
          linkSection: window?.pageInfoGlobal?.pageInfo?.pageSection || '',
          linkName: 'sort fonts',
          linkType: LINK_TYPES.BUTTON
        }
      }
      adobeCjaEventEmitter(sortEventAdobePayload)
    }
    if (this.results.items.length > 0) {
      const results = { ...this.results }
      let uploadedFonts = results.items
      let fontTypeTTFV = []
      let fontTypeOther = []
      uploadedFonts.forEach((val) => {
        if (val.fontInfo.formatFlavor === 'TTFV') {
          fontTypeTTFV.push(val)
        } else {
          fontTypeOther.push(val)
        }
      })
      fontTypeTTFV = this.sortOrder(fontTypeTTFV)
      fontTypeOther = this.sortOrder(fontTypeOther)
      const sortedFonts = [...fontTypeTTFV, ...fontTypeOther]
      // eslint-disable-next-line no-undef
      let sortedResults = { items: flattenDeep(sortedFonts), timer: TimeoutUtil() }
      this.results = sortedResults
      this.$events.$emit('update:external_sku')
      this.updateSubmission()
    }
  },
  sortOrder(uploadFonts) {
    let z = uploadFonts.sort((a, b) => (importOrder.indexOf(a.fontInfo.slope) - importOrder.indexOf(b.fontInfo.slope))).sort((a, b) => a.fontInfo.weightClass - b.fontInfo.weightClass).sort((a, b) => a.fontInfo.widthClass - b.fontInfo.widthClass).sort((a, b) => (designOrder.indexOf(a.fontInfo.design) - designOrder.indexOf(b.fontInfo.design)))
    return z
  },
  unsetSmartSort() {
    this.updateExSkuOnDrag()
    this.$events.$emit('unset_smart_sort')
  },
  setSubscriptions() {
    /**
     * Listen to $store mutations.
     */
    this.unsubscribe = this.$store.subscribe((mutation, state) => {
      if (mutation.type === 'binaries/uploadComplete' && this.submission.status === 'live') {
        this.setResults()
        this.unsetSmartSort()
      }
      if (mutation.type === 'binaries/uploadComplete' && this.submission.status !== 'live') {
        this.setResults().then(() => {
          this.setSkuId()
          this.updateSubmission().then(this.emitSkuUpdate)
          this.unsetSmartSort()
        })
      }
      if (mutation.type === 'submissions/updateStatus') {
        this.emitResultsUpdate()
      }
    })
  },
  prepareCompareBinaries() {
    return new Promise(resolve => {
      this.binaries2compare = this.submissionBinaries
      resolve()
    })
  },
  compareBinaries() {
    return new Promise(resolve => {
      /**
       * compare objects, using Lodash library (https://lodash.com/)
       */
      const equal = isEqual(this.binaries2compare, this.submissionBinaries)
      resolve(!equal)
    })
  },
  getStyleNames() {
    /**
     * Generate a list of existing style names for custom (Vee-)validation
     * It has package size excluding custom pack, pack names excluding custom pack, and style names from binary services
     */
    const stylesNames = this.results.items.map(p => p.styleName)
    const excludeCP = this.submissionPackages.filter(item => item.packageType !== 'customPackage')
    const excludeCPSize = excludeCP.length
    const excludeCPNames = excludeCP.map(p => {
      if (!isEmpty(p.styles)) {
        return Misc.replaceString(p.name || p.styles[0].binaryName, this.submission.userMetaData.family.name, '')
      }
    })
    return [excludeCPSize, ...excludeCPNames, ...stylesNames]
  },
  handleMouseOver() {
    /**
     * Mouse Over Handler
     * */
    this.isDragDropDisable = true
  },
  handleMouseOut() {
    /**
     * Mouse Out Handler
     * */
    if (this.isDragDropDisable) this.isDragDropDisable = false
  },
  /**
   * Create default style name with format: (Family Name + Style Name) - (Parent Family name)
   * Removes parent family name from the combined style if present
   * 
   * Examples:
   * - Input: familyName="Helvetica", styleName="Bold", parentFamily="Arial"
   *   Output: "Helvetica Bold - Arial"
   * 
   * - Input: familyName="Arial", styleName="Bold", parentFamily="Arial" 
   *   Output: "Bold - Arial" (removes duplicate "Arial" from beginning)
   * 
   * - Input: familyName="Arial Bold", styleName="Italic", parentFamily="Arial"
   *   Output: "Bold Italic - Arial" (removes "Arial" prefix)
   * 
   * @param {Object} element - The font element containing familyName and fontInfo
   * @param {string} parentFamilyName - The parent family name
   * @returns {string} - Formatted style name
   */
  createDefaultStyleName(element, parentFamilyName) {
    // Format: (Family Name + Style Name) - (Parent Family name)
    const familyName = element.familyName || ''
    const styleName = element.fontInfo?.styleName || ''
    const parentFamily = parentFamilyName || ''

    // Combine family name and style name
    let fullStyleName = `${familyName} ${styleName}`.trim()

    // Remove parent family name from fullStyleName if present
    if (parentFamily && fullStyleName.startsWith(parentFamily)) {
      fullStyleName = fullStyleName.substring(parentFamily.length).trim()
    }

    // Create the final format with parent family name
    const defaultStyleName = fullStyleName

    return defaultStyleName
  },

  /**
   * Get formatted default style name for StyleTextBox component
   * This version ensures Vue reactivity by explicitly depending on this.family.name
   * @param {Object} element - The font element from results.items
   * @returns {string} - Formatted default style name
   */
  getFormattedDefaultStyleName(element) {
    if (!element?.fontInfo) {
      return ''
    }

    if (element.styleName && element.styleName !== element.fontInfo?.styleName) {
      return element.styleName
    }

    // Create the element structure for the formatting function
    const fontElement = {
      familyName: element.familyName || element.fontInfo.familyName,
      fontInfo: {
        styleName: element.fontInfo.styleName
      }
    }

    // Determine the parent family name - explicitly reference this.family.name for Vue reactivity
    let parentFamilyName = this.family?.name || ''

    // If family name is not set yet (during initial upload), use the first font's family name
    if (!parentFamilyName && this.results?.items?.length > 0) {
      const firstFont = this.results.items[0]
      parentFamilyName = firstFont.familyName || firstFont.fontInfo?.familyName || ''
    }

    // Apply the default style name formatting
    return this.createDefaultStyleName(fontElement, parentFamilyName)
  },

  /**
   * Get reactive style name for an element
   * This method ensures the style name updates when family name changes
   * @param {Object} element - The font element from results.items
   * @returns {string} - Current style name for the element
   */
  getReactiveStyleName(element) {
    return this.getFormattedDefaultStyleName(element)
  },
}
